import { DataGrid } from '@cimplico/components';
import { useFormContext, useWatch } from 'react-hook-form';
import { WorksheetWithAccount } from '../../types/worksheet';
import { useEffect, useState } from 'react';
import { Account, AccountClass } from '../../types/account';
import { JournalLinesFields, JournalTaxTypeEnum } from '../../types/journal';
import { calcJournalTotal, calcRemainingAmountToDisplay } from './utils';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { WorkpaperSourceEnum } from '../../types/workpaper';
import CreditField from './journal-row-column/CreditField';
import DebitField from './journal-row-column/DebitField';
import TaxRateField from './journal-row-column/TaxRateField';
import AccountField from './journal-row-column/AccountField';
import DeleteButton from './journal-row-column/DeleteButton';
import DescriptionField from './journal-row-column/DescriptionField';
import { AccountClassification, EntityAccount } from '../../types/entity';

type NewOrEditJournalLineRowProps = {
    index: number;
    id: string;
    entityId: string;
    canRemove: boolean;
    worksheet?: WorksheetWithAccount;
    accounts?: EntityAccount[];
    amountEditable?: boolean;
    onDelete: (index: number) => void;
};

function NewOrEditJournalLineRow({
    id,
    index,
    entityId,
    canRemove,
    worksheet,
    accounts,
    amountEditable = true,
    onDelete,
}: NewOrEditJournalLineRowProps): JSX.Element {
    const { control, getValues, setValue } = useFormContext();
    const { provider } = useWorkpaperContext();

    const [accountClass, setAccountClass] = useState<AccountClassification | undefined>(
        getValues(`lines.${index}.account`)
            ? (accounts?.find((acc) => acc.id === getValues(`lines.${index}.account`))?.class as AccountClassification)
            : !amountEditable && worksheet?.account!.class
            ? (worksheet.account!.class as AccountClassification)
            : undefined
    );

    const taxType = useWatch({ control, name: `taxType` });
    const showTaxRate = taxType && taxType !== JournalTaxTypeEnum.NO_TAX && provider === WorkpaperSourceEnum.XERO;

    function populateAmount(): void {
        const journalLines: JournalLinesFields[] = getValues('lines');
        const nonEditableLastLine = journalLines.find((l) => l?.amountEditable === false);

        const { debitRemaining, creditRemaining } = calcRemainingAmountToDisplay(
            taxType,
            journalLines,
            nonEditableLastLine || getValues(`lines.${index}`)
        );

        if (nonEditableLastLine) {
            setValue(`lines.${getValues('lines').length - 1}.debit`, debitRemaining);
            setValue(`lines.${getValues('lines').length - 1}.credit`, creditRemaining);
        } else if (getValues(`lines.${index}.debit`) === null && getValues(`lines.${index}.credit`) === null) {
            setValue(`lines.${index}.debit`, debitRemaining);
            setValue(`lines.${index}.credit`, creditRemaining);
        }

        updateJournalTotalDisplay();
    }

    function updateJournalTotalDisplay(): void {
        const journalLines: JournalLinesFields[] = getValues('lines');
        const total = calcJournalTotal(taxType, journalLines);

        setValue('debitTotal', total.debitTotal);
        setValue('creditTotal', total.creditTotal);
        setValue('debitSubtotal', total.debitSubtotal);
        setValue('creditSubtotal', total.creditSubtotal);
        setValue('debitTaxTotal', total.debitTaxTotal);
        setValue('creditTaxTotal', total.creditTaxTotal);
        setValue('valueOutBy', Math.round((total.debitTotal - total.creditTotal) * 100) / 100);
    }

    useEffect(() => {
        if (!accounts?.find((acc) => acc.id === getValues(`lines.${index}.account`))?.canJournal) {
            setValue(`lines.${index}.account`, null);
        }
    }, [accounts]);

    return (
        <DataGrid.Row id={id} preventDrag={!amountEditable}>
            <div className="flex flex-col gap-2 lg:gap-4 lg:flex-row w-full mb-2">
                <div className={`-ml-4 lg:ml-0 py-1 md:py-0 grid gap-2 w-full items-center lg:gap-4 grid-cols-2`}>
                    <DescriptionField index={index} />
                    <AccountField
                        index={index}
                        accounts={accounts}
                        amountEditable={amountEditable}
                        onAccountChange={setAccountClass}
                        onPopulate={populateAmount}
                        worksheet={worksheet}
                    />
                </div>
                <div className={`-ml-4 lg:ml-0 grid gap-2 w-full items-center lg:gap-4 ${showTaxRate ? 'grid-cols-3' : 'grid-cols-2'}`}>
                    {showTaxRate && (
                        <TaxRateField index={index} onPopulate={populateAmount} entityId={entityId} accountClass={accountClass} />
                    )}
                    <DebitField index={index} onPopulate={populateAmount} amountEditable={amountEditable} />
                    <CreditField index={index} onPopulate={populateAmount} amountEditable={amountEditable} />
                </div>
            </div>
            {amountEditable && (
                <DeleteButton
                    disabled={!canRemove || !amountEditable}
                    onDelete={onDelete}
                    onPopulate={populateAmount}
                    onUpdateTotals={updateJournalTotalDisplay}
                    index={index}
                />
            )}
        </DataGrid.Row>
    );
}

export default NewOrEditJournalLineRow;
